import { type FormEvent, useEffect, useState } from "react";
import { useNavigate, useParams } from "@tanstack/react-router";
import {
  type TenantSummary,
  disableTenant,
  enableTenant,
  getSuperadminApi,
  listTenants,
} from "@praesto/core";
import { Toolbar, Button, Modal, FormField, Input, Badge, SegmentedControl } from "@praesto/ui-kit";
import { type TenantRow, type TrialFilter, filterByTrial, trialLabel } from "./tenant-trial";

export function TenantsTable(props: {
  onCreate: () => void;
  onImpersonate: (tid: string) => void;
}) {
  const navigate = useNavigate();
  const { tenantSlug } = useParams({ strict: false }) as { tenantSlug: string };
  const [tenants, setTenants] = useState<ReadonlyArray<TenantRow>>([]);
  const [trialFilter, setTrialFilter] = useState<TrialFilter>("all");
  const [error, setError] = useState<string | null>(null);
  const [reload, setReload] = useState(0);
  const [toggleTarget, setToggleTarget] = useState<TenantSummary | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const api = getSuperadminApi();
      const tok = await api.getAuthToken();
      const r = await listTenants(api.apiBase, tok);
      if (!cancelled) setTenants(r.tenants as ReadonlyArray<TenantRow>);
    })().catch((e) => {
      if (!cancelled) setError(String(e));
    });
    return () => {
      cancelled = true;
    };
  }, [reload]);

  const shown = filterByTrial(tenants, trialFilter);
  const expiredCount = tenants.filter((t) => t.trial?.expired === true).length;

  return (
    <div>
      <Toolbar
        title="Tenants"
        actions={
          <Button size="sm" variant="primary" onClick={props.onCreate}>
            + Nuovo tenant
          </Button>
        }
      />
      {error && <p className="text-sm text-danger mb-3">{error}</p>}
      {/* Trials are 30 days and nothing suspends a tenant on its own: this
          filter is how an operator finds who is past the end, to invoice them
          or to disable them by hand. */}
      <SegmentedControl<TrialFilter>
        ariaLabel="Filtra per prova gratuita"
        size="sm"
        className="mb-3"
        value={trialFilter}
        onChange={setTrialFilter}
        options={[
          { value: "all", label: "Tutti" },
          { value: "trial", label: "In prova" },
          { value: "expired", label: `Prova scaduta (${expiredCount})` },
        ]}
      />
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-eyebrow text-left">
              <th className="hidden py-2 sm:table-cell">ID</th>
              <th>Nome</th>
              <th>Status</th>
              <th className="hidden sm:table-cell">Prova</th>
              <th>Utenti</th>
              <th>Moduli</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {shown.map((t) => (
              <tr key={t.id} className="border-t border-border-default">
                <td className="hidden text-mono-xs py-2 sm:table-cell">{t.id}</td>
                <td className="py-2">
                  {t.displayName}
                  {/* On a phone the trial column is scrolled out of view, and
                      it is the reason to open this list: say it under the name. */}
                  {t.trial && (
                    <div className="sm:hidden">
                      <TrialCell trial={t.trial} />
                    </div>
                  )}
                </td>
                <td>
                  <Badge
                    tone={t.status === "active" ? "success" : "danger"}
                    variant="soft"
                    size="xs"
                  >
                    {t.status}
                  </Badge>
                </td>
                <td className="hidden whitespace-nowrap sm:table-cell">
                  <TrialCell trial={t.trial} />
                </td>
                <td className="font-mono whitespace-nowrap">{t.userCount}</td>
                <td className="text-mono-xs text-fg-muted">{t.activeModules.join(", ")}</td>
                <td className="text-right space-x-1">
                  {/* Now that sections are real routes, one screen can hand off
                    to another with the filter already applied — this is the
                    question you actually ask about a customer ("is our mail
                    reaching them?") without retyping anything. */}
                  <Button
                    size="sm"
                    variant="ghost"
                    title="Email inviate a questa struttura"
                    onClick={() =>
                      void navigate({
                        to: `/${tenantSlug}/emails`,
                        search: { tenantId: t.id, reason: `struttura ${t.id}` },
                      } as never)
                    }
                  >
                    Email
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => props.onImpersonate(t.id)}>
                    Impersona
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-danger"
                    onClick={() => setToggleTarget(t)}
                  >
                    {t.status === "active" ? "Disabilita" : "Riattiva"}
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {shown.length === 0 && trialFilter !== "all" && (
        <p className="text-sm text-fg-muted py-3">
          {trialFilter === "expired"
            ? "Nessun tenant ha superato i 30 giorni di prova."
            : "Nessun tenant è in prova adesso."}
        </p>
      )}
      {toggleTarget && (
        <ToggleStatusModal
          tenant={toggleTarget}
          onClose={() => setToggleTarget(null)}
          onDone={() => {
            setToggleTarget(null);
            setReload((r) => r + 1);
          }}
        />
      )}
    </div>
  );
}

function TrialCell({ trial }: { trial: TenantRow["trial"] }) {
  const label = trialLabel(trial);
  if (label === null) return null;
  return trial?.expired ? (
    <Badge tone="warning" variant="soft" size="xs">
      {label}
    </Badge>
  ) : (
    <span className="text-fg-muted">{label}</span>
  );
}

function ToggleStatusModal(props: {
  tenant: TenantSummary;
  onClose: () => void;
  onDone: () => void;
}) {
  const willDisable = props.tenant.status === "active";
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const api = getSuperadminApi();
      const tok = await api.getAuthToken();
      if (willDisable) {
        await disableTenant(api.apiBase, props.tenant.id, { reason }, tok);
      } else {
        await enableTenant(api.apiBase, props.tenant.id, { reason }, tok);
      }
      props.onDone();
    } catch (e2) {
      setError(String(e2));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      open={true}
      onClose={props.onClose}
      title={`${willDisable ? "Disabilita" : "Riattiva"} ${props.tenant.displayName}`}
      size="sm"
    >
      <form onSubmit={submit} className="space-y-3">
        <FormField label="Reason (obbligatoria, min 3 caratteri)">
          <Input
            value={reason}
            required
            minLength={3}
            placeholder="Ticket #..."
            onChange={(e) => setReason(e.target.value)}
          />
        </FormField>
        <div className="flex gap-2 justify-end">
          <Button type="button" size="sm" variant="ghost" onClick={props.onClose}>
            Annulla
          </Button>
          <Button
            type="submit"
            size="sm"
            variant={willDisable ? "danger" : "primary"}
            disabled={busy || reason.length < 3}
          >
            {busy ? "…" : willDisable ? "Disabilita" : "Riattiva"}
          </Button>
        </div>
        {error && <p className="text-sm text-danger">{error}</p>}
      </form>
    </Modal>
  );
}
